import PublicAppVars from "utils/PublicAppVars.js";
import { findPrimaryToken } from 'components/manage-cards/TokenHelpers.js';
import { CARD_TYPE_CONTACTLESS, MEDIA_TYPE_STANDARD_BANKCARD } from 'utils/Constants.js';
import useStdQuery from "components/data/hooks/useStdQuery.js";
import { GET_TRANSIT_ACCOUNT, getTransitAccountDefaultRefetchVariables } from "./TransitAccount.js";



export const isEmv = (tokens) => {
	const primaryToken = findPrimaryToken(tokens);
	return primaryToken?.subsystemTokenType === CARD_TYPE_CONTACTLESS && primaryToken?.mediaType === MEDIA_TYPE_STANDARD_BANKCARD;
};

// Because an unreg user will not have a customerId we cannot get the TA data from the cache
// We have to request the transitAccount resolver instead
const getQueryArgs = ({ subsystemAccountReference, queryOptions }) =>
	({ query: GET_TRANSIT_ACCOUNT, options:{ variables: getTransitAccountDefaultRefetchVariables(subsystemAccountReference), ...queryOptions } });

const useTransitAccountEMVData = ({ subsystemAccountReference, queryOptions = {} }) => {
	const { query, options }  = getQueryArgs({  subsystemAccountReference, queryOptions });

	// If there is no login stage then we are in a race condition and we don't want to fire this query.
	const { data, loading, error } = useStdQuery(query, { ...options });

	// But to be safe we'll double check it off the subsystemAccountReference incase that ever changes in the future
	const transitAccountQ = data?.transitAccountQ;

	return {
		tokens: transitAccountQ?.tokens ?? [],
		purses: transitAccountQ?.purses ?? [],
		error,
		loading,
	};
};

// Check if the token is EMV and if reload is enabled
export const useEmvCanReload = ({ subsystemAccountReference }) => {
	const { tokens, loading } = useTransitAccountEMVData({ subsystemAccountReference });

	if (loading) {
		return false;
	}

	if (isEmv(tokens)) {
		return PublicAppVars.ENABLE_EMV_RELOAD;
	}

	return true;
};

// Check if the token is EMV and if autoload is enabled
export const useEmvCanAutoload = ({ subsystemAccountReference, queryOptions = {} }) => {
	const { tokens, loading } = useTransitAccountEMVData({
		subsystemAccountReference,
		queryOptions,
	});

	if (loading) {
		return false;
	}

	if (isEmv(tokens)) {
		return PublicAppVars.ENABLE_EMV_AUTOLOAD;
	}

	return true;
};

// Checks if the card is EMV & returns a boolean
export const useIsEmvCard = ({ subsystemAccountReference }) => {
	const { tokens, loading } = useTransitAccountEMVData({
		subsystemAccountReference,
		queryOptions : { skip: !subsystemAccountReference },
	 });

	if (loading) {
		return false;
	}

	return isEmv(tokens);
};

// Check if the token is EMV, reload is enabled, & there is a balance to resolve
// If the EMV card is negative, the user should be able to pay off the balance.
export const useCanResolveBalance = ({ subsystemAccountReference }) => {
	const { tokens, purses, loading } = useTransitAccountEMVData({ subsystemAccountReference });
	const emvCanReload = useEmvCanReload({ subsystemAccountReference });

	if (loading) {
		return false;
	}

	// Card is EMV
	if (isEmv(tokens)) {
		const purseTotal = (purses?.reduce((acc, purse) => acc + purse.balance, 0)) ?? 0;

		// Token is Emv & there is a balance that needs to be resolved.
		if (purseTotal < 0) {
			return true;
		}
		// Default state fall back to the emv logic
		return emvCanReload;
	} else {
		return true;
	}
};

export const useCanViewBalance = ({ subsystemAccountReference }) => {
	const { tokens, purses, loading } = useTransitAccountEMVData({ subsystemAccountReference });
	const emvCanReload = useEmvCanReload({ subsystemAccountReference });

	if (loading) {
		return false;
	}

	// Card is EMV
	if (isEmv(tokens)) {
		const purseTotal = (purses?.reduce((acc, purse) => acc + purse.balance, 0)) ?? 0;

		// As long as the purse balance is not 0 we want to show it.
		if (purseTotal !== 0) {
			return true;
		}
		// Default state fall back to the emv logic
		return emvCanReload;
	} else {
		return true;
	}
};
